import type { LocaleObject } from "@nuxtjs/i18n/dist/module";
import { useQueryClient } from "@tanstack/vue-query";
import type { Category, EventDetails, ProductDetails } from "mws-ts-v2-sdk";
import { getFormattedCurrencyAmount } from "mws-utils/lib";

import CallToActionToast from "~/components/base/feedback/BaseCallToActionToast.vue";
import { bidQueryKeys } from "~/queries/catalog/bids";
import { useRefetchEventWithProductsInfiniteQuery } from "~/queries/catalog/events";
import { followedCategoriesListQueryKeys } from "~/queries/catalog/mePreferenceCategories";
import { followedEventsListQueryKeys } from "~/queries/catalog/mePreferenceEvents";
import { followedProductsListQueryKeys } from "~/queries/catalog/mePreferenceProducts";
import { tokensQueryKeys } from "~/queries/finance/tokens";
import type { Bid } from "~/types/Catalog";

export type BidUpdateData = {
  type: string;
  payload: {
    productSkuId: string;
    productSlug: string;
    productId: string;
    eventSlug: string;
    newBid: Bid;
    updatedProductEndDate: string;
    biddingUserId: string;
    productListingId: string;
    eventId: string;
  };
};

export type TokenUpdateData = {
  type: string;
  payload: { tokenId: string };
};

const encrypt = async (message: string): Promise<string> => {
  const msgUint8 = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  return hashArray
    .map((b) => b.toString())
    .join("")
    .slice(0, 8);
};

export const calculateObfuscatedUserId = (
  userId: string,
  productId: string,
): Promise<string> => {
  return encrypt(userId + productId);
};

/**
 * Starts the signalR connection to the server if the platform supports it
 * and returns the connection object
 */
export const useRealtimeConnection = createSharedComposable(() => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { $_connection } = useNuxtApp();

  const platformStore = usePlatformStore();
  const isAuctionsEnabled = computed(
    () => platformStore.getPlatformConfiguration.isAuctionsEnabled,
  );

  async function start() {
    try {
      await $_connection?.start();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error connecting to real-time updates", error);
    }
  }

  $_connection?.onclose(async () => {
    await start();
  });

  watchEffect(() => {
    if (isAuctionsEnabled.value) {
      start();
    }
  });

  return $_connection;
});

const useRealTimeUpdates = <T>(
  connectionString: Ref<string | undefined>,
  onUpdateCallback: (data: T) => void,
  options?: {
    disabled: Ref<boolean>;
  },
) => {
  const connection = useRealtimeConnection();

  const isDisabled = computed(() => Boolean(options?.disabled.value));

  watch(
    [connectionString, isDisabled],
    async ([newConnectionString, newIsDisabled]) => {
      if (newIsDisabled || !newConnectionString) return;

      await sleep(1000);
      connection?.on(newConnectionString, async (data: T) => {
        onUpdateCallback(data);
      });
    },
    { immediate: true, flush: "post" },
  );

  onBeforeUnmount(() => {
    if (connectionString.value) {
      connection?.off(connectionString.value);
    }
  });
};

export const usePrivateUpdates = async () => {
  const toast = useToast();
  const { locale, locales, t } = useI18n();
  const { activeCurrency } = useActiveCurrency();
  const route = useRoute();
  const queryClient = useQueryClient();
  const refetchEventWithProducts = useRefetchEventWithProductsInfiniteQuery();

  const localeInformation = locales.value.find((info) => {
    return typeof info !== "string" && info.code === locale.value;
  }) as LocaleObject | undefined;

  const intlLocale = localeInformation?.language || locale.value;

  const connectionString = ref("private");

  useRealTimeUpdates<BidUpdateData>(connectionString, (data) => {
    if (data.type === "UserWasOutbid") onUserWasOutbid(data);

    if (data.type === "BidPlaced") onMyPrivateProductUpdates(data);
  });

  useRealTimeUpdates<TokenUpdateData>(connectionString, (data) => {
    if (data.type === "AdyenTokenSet") onPaymentTokensUpdated();
  });

  const onPaymentTokensUpdated = () => {
    queryClient.invalidateQueries({ queryKey: tokensQueryKeys._def });
  };

  // Private products are: your bids, your max bids, and your followed items
  const onMyPrivateProductUpdates = ({ payload }: BidUpdateData) => {
    queryClient.invalidateQueries({
      queryKey: bidQueryKeys.max._def,
    });

    queryClient.invalidateQueries({
      queryKey: ["me", "bids", "infiniteList"],
    });

    queryClient.invalidateQueries({
      queryKey: followedProductsListQueryKeys._def,
    });

    queryClient.invalidateQueries({
      queryKey: followedCategoriesListQueryKeys._def,
    });

    queryClient.invalidateQueries({
      queryKey: followedEventsListQueryKeys._def,
    });

    queryClient.invalidateQueries({
      queryKey: [
        "products",
        "infiniteList",
        { eventIdentifier: payload.eventSlug },
      ],
    });

    if (payload.eventId) {
      refetchEventWithProducts(payload.eventId);
    }
  };

  const onUserWasOutbid = ({ payload }: BidUpdateData) => {
    // if you are on the product page, you will already receive new bid messages
    if (route.params.productIdentifier) return;

    const price = getFormattedCurrencyAmount(
      locale.value,
      activeCurrency.value.isoCurrencyCode,
      payload.newBid.amounts[activeCurrency.value.isoCurrencyCode]!,
    );

    const localizedEndDate = Intl.DateTimeFormat(intlLocale, {
      // e.g. "19/12/2020, 22:23:56"
      dateStyle: "short",
      timeStyle: "medium",
    }).format(new Date(payload.updatedProductEndDate));

    const translatedMessage = t("product.outbid", {
      price,
      newEndDate: localizedEndDate,
    });

    toast.error({
      component: CallToActionToast,
      props: {
        htmlMessage: translatedMessage,
        callToAction: {
          text: t("navigation.goToProduct"),
          path: `/product/${payload.productListingId}`,
        },
      },
    });
  };
};

export const useProductUpdates = async (
  product: Ref<ProductDetails | undefined>,
) => {
  const i18n = useI18n();
  const toast = useToast();

  const { activeCurrency } = useActiveCurrency();

  const updatePlatformAfterBid = useUpdatePlatformAfterBid();

  const connectionString = computed(() =>
    product.value?.id ? `products/${product.value.id}` : undefined,
  );

  useRealTimeUpdates<BidUpdateData>(connectionString, (data) => {
    if (data.type === "BidPlaced") {
      onProductUpdate(data);
    }
  });

  const { userId } = useUser();

  const onProductUpdate = async ({ payload }: BidUpdateData) => {
    if (!product.value) return;

    const locale = i18n.locale.value;
    const localeInformation = i18n.locales.value.find((info) => {
      return typeof info !== "string" && info.code === locale;
    }) as LocaleObject | undefined;
    const intlLocale = localeInformation?.language || locale;

    updatePlatformAfterBid({
      productId: payload.productSkuId,
      productSlug: payload.productSlug,
      eventSlug: payload.eventSlug,
      updateMaxBid: product.value.allowMaxBidding,
    });

    const isCurrentUserTheBidder =
      !!userId.value &&
      parseInt(
        await calculateObfuscatedUserId(
          userId.value, // auth.user has no typing, so cast.
          payload.productSkuId,
        ),
      ) === payload.newBid.obfuscatedUserId;

    if (isCurrentUserTheBidder) return; // the logic for user's own bid happens after the post-bid call

    const price = getFormattedCurrencyAmount(
      i18n.locale.value,
      activeCurrency.value.isoCurrencyCode,
      payload.newBid.amounts[activeCurrency.value.isoCurrencyCode]!,
    );

    let translatedMessage = i18n.t("product.newBidPlaced", {
      price,
    });

    if (new Date(payload.updatedProductEndDate) > product.value.endDate) {
      const localizedEndDate = Intl.DateTimeFormat(intlLocale, {
        // e.g. "19/12/2020, 22:23:56"
        dateStyle: "short",
        timeStyle: "medium",
      }).format(new Date(payload.updatedProductEndDate));

      translatedMessage = i18n.t("product.newBidPlacedExtended", {
        price,
        newEndDate: localizedEndDate,
      });
    }

    toast.info(
      {
        component: CallToActionToast,
        props: {
          htmlMessage: translatedMessage,
        },
      },
      { id: payload.newBid.id },
    );
  };
};

export const useCategoryUpdates = async (
  category: Ref<Category | undefined>,
) => {
  const updatePlatformAfterBid = useUpdatePlatformAfterBid();

  const connectionString = computed(() =>
    category.value?.id ? `categories/${category.value.id}` : undefined,
  );

  useRealTimeUpdates<BidUpdateData>(connectionString, (data) => {
    onCategoryUpdate(data);
  });

  const onCategoryUpdate = ({ payload }: BidUpdateData) => {
    if (!category.value?.id) return;

    updatePlatformAfterBid({
      productId: payload.productSkuId,
      productSlug: payload.productSlug,
      eventSlug: payload.eventSlug,
      categoryId: category.value.id,
    });
  };
};

export const useEventUpdates = async (
  event: Ref<EventDetails | undefined>,
  options?: {
    disabled: Ref<boolean>;
  },
) => {
  const updatePlatformAfterBid = useUpdatePlatformAfterBid();

  const connectionString = computed(() =>
    event.value?.id ? `events/${event.value.id}` : undefined,
  );

  useRealTimeUpdates<BidUpdateData>(
    connectionString,
    (data) => onEventUpdate(data),
    options,
  );

  const onEventUpdate = async ({ payload }: BidUpdateData) => {
    if (!event.value) return;

    updatePlatformAfterBid({
      productId: payload.productSkuId,
      productSlug: payload.productSlug,
      eventSlug: payload.eventSlug,
    });
  };
};
